import withQuery from 'with-query';

import packageJson from '../_package.json';
import DLErrors, { DLError, mapNetworkError } from '../errors';
import { UserResponse } from '../py-generated/datalouder/third_party_login/types';

export { postOptionWithBody, getOption, getPromise, postPromise };

const deriveDlError = (response: Response): DLError | null => {
  if (response.status === 400) {
    // unauthorized
    return new DLErrors.InvalidRequest();
  }

  if (response.status === 401) {
    // unauthorized
    return new DLErrors.UserTokenInvalid();
  }

  if (response.status === 403) {
    return new DLErrors.UserNotMember();
  }

  if (response.status === 404) {
    // unauthorized
    return new DLErrors.NotFound();
  }

  if (response.status === 426) {
    return new DLErrors.ClientOutOfDate();
  }
  if (response.status === 502) {
    return new DLErrors.BadGateway();
  }
  if (response.status === 500) {
    return new DLErrors.InternalServerError();
  }
  if (response.status === 429) {
    return new DLErrors.ThrottleError();
  }
  return null;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function handleJSONResponse<T>(
  transformer?: (data: any) => T
): (response: any) => Promise<T> {
  if (!transformer) transformer = x => x;
  return response => {
    return response.text().then(text => {
      if (!response.ok) {
        // See if it is in our new "CustomerException" format
        let localizedMessages: { en: string; zh: string } = null;
        try {
          const responseJson = JSON.parse(text);
          if (
            responseJson['error'] &&
            Object.keys(responseJson['error']).includes('en') &&
            Object.keys(responseJson['error']).includes('zh')
          ) {
            localizedMessages = responseJson['error'];
          }
        } catch {
          // do nothing
        }

        const dlError = deriveDlError(response);
        if (dlError) {
          if (localizedMessages) {
            dlError.setLocalizedErrorMessage(
              localizedMessages.en,
              localizedMessages.zh
            );
          }
          throw dlError;
        }
        const data = text && JSON.parse(text);
        throw new Error((data && data.message) || response.statusText);
      }
      const data = text && JSON.parse(text);

      return transformer(data);
    });
  };
}

function postOptionWithBody<T>(
  bodyObject: T,
  abortSignal?: AbortSignal
): RequestInit {
  return {
    method: 'POST',
    signal: abortSignal,
    headers: {
      'Content-Type': 'application/json',
      ...authorizationHeaders(),
      ...clientVersionHeaders(),
    },
    body: JSON.stringify(bodyObject),
  };
}

function getOption(abortSignal?: AbortSignal): RequestInit {
  return {
    method: 'GET',
    signal: abortSignal,
    headers: {
      'Content-Type': 'application/json',
      ...authorizationHeaders(),
      ...clientVersionHeaders(),
    },
  };
}

// eslint-disable-next-line @typescript-eslint/ban-types
function getPromise<T extends object, U>(
  url: string,
  query: T,
  transformer?: (data: any) => U,
  abortSignal?: AbortSignal
): Promise<U> {
  return fetch(withQuery(url, query), getOption(abortSignal))
    .then(handleJSONResponse<U>(transformer))
    .catch(mapNetworkError);
}

function postPromise<T, U>(
  url: string,
  body: T,
  transformer?: (data: any) => U,
  abortSignal?: AbortSignal
): Promise<U> {
  return fetch(url, postOptionWithBody(body, abortSignal))
    .then(handleJSONResponse(transformer))
    .catch(mapNetworkError);
}

function authorizationHeaders() {
  try {
    const { login, authToken } = JSON.parse(
      localStorage.user
    ) as UserResponse & { authToken: string };
    return {
      Authorization:
        'DATALOUDER ' + JSON.stringify({ email: login, tokenKey: authToken }),
    };
  } catch {
    return {};
  }
}

function clientVersionHeaders() {
  return {
    'Datalouder-Client-Build': String(packageJson.buildNumber),
    'Datalouder-Client-Version': packageJson.version,
  };
}

export type RequestOptions = RequestInit & { url: string };
